import { Link, useNavigate } from "react-router-dom";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowUpRight, Trash2, Copy } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";

interface RecentAnalysis {
  id: string;
  url: string;
  keyword: string;
  timestamp: number;
  seo_score?: number;
}

interface AnalysisTableRowProps {
  analysis: RecentAnalysis;
  onDelete: (e: React.MouseEvent, analysis: RecentAnalysis) => void;
  selected: boolean;
  onSelect: (id: string, checked: boolean) => void;
}

export const AnalysisTableRow = ({ 
  analysis, 
  onDelete, 
  selected,
  onSelect 
}: AnalysisTableRowProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleRowClick = () => {
    navigate(`/relatorio/${analysis.id}`);
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/relatorio/${analysis.id}`;
    navigator.clipboard.writeText(url).then(() => {
      toast({
        title: "Link copiado para a área de transferência",
        duration: 2000,
      });
    });
  };

  const truncateUrl = (url: string) => {
    return url.length > 65 ? `${url.substring(0, 65)}...` : url;
  };

  const truncateKeyword = (keyword: string) => {
    return keyword.length > 20 ? `${keyword.substring(0, 20)}...` : keyword;
  };

  return (
    <TableRow 
      className="cursor-pointer hover:bg-gray-50"
      onClick={handleRowClick}
    >
      <TableCell className="px-8">
        <Checkbox 
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onCheckedChange={(checked) => onSelect(analysis.id, checked as boolean)}
        />
      </TableCell>
      <TableCell className="break-all px-8">
        <div className="max-w-[200px] sm:max-w-none truncate font-normal" title={analysis.url}>
          {truncateUrl(analysis.url)}
        </div>
      </TableCell>
      <TableCell className="break-all px-8">
        <div className="max-w-[150px] sm:max-w-none truncate" title={analysis.keyword}>
          {truncateKeyword(analysis.keyword)}
        </div>
      </TableCell>
      <TableCell className="text-center px-8">
        {analysis.seo_score ? Math.round(analysis.seo_score) : '-'}
      </TableCell>
      <TableCell className="hidden sm:table-cell whitespace-nowrap px-8">
        {`há ${formatDistanceToNow(analysis.timestamp, { 
          locale: ptBR,
          addSuffix: false
        })}`}
      </TableCell>
      <TableCell className="hidden sm:table-cell px-8">
        <Link 
          to={`/relatorio/${analysis.id}`}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 whitespace-nowrap"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
        >
          Ver análise
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </TableCell>
      <TableCell className="px-8">
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyLink}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Copiar link da análise"
          >
            <Copy className="w-4 h-4 text-gray-500 hover:text-blue-500" />
          </button>
          <button
            onClick={(e) => onDelete(e, analysis)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Excluir análise"
          >
            <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-500" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
};